import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../Services/styles/ServicesPage.css";
import "./styles/EmailsPage.css";
import showToast from "../../utils/toast";
import Pagination, { MAX_PAGE_SIZE } from "../../components/Base/Pagination";
import ServiceEmailsTable, {
  ServiceEmailsTableFilters,
} from "../../components/Services/tables/ServiceEmailsTable";
import ServiceEmailsSimpleTable from "../../components/Services/tables/ServiceEmailsSimpleTable";
import ServiceEmailSolicitudesTable, {
  ServiceEmailSolicitudesTableFilters,
} from "../../components/Services/tables/ServiceEmailSolicitudesTable";
import EmailsMonitorSidePanel from "../../components/Services/tables/EmailsMonitorSidePanel";
import ServiceEmailRequestModal from "../../components/Services/forms/ServiceEmailRequestModal";
import ServiceEmailBulkModal from "../../components/Services/forms/ServiceEmailBulkModal";
import ServiceEmailCommentModal from "../../components/Services/forms/ServiceEmailCommentModal";
import EmailsSimpleExportModal from "../../components/Services/forms/EmailsSimpleExportModal";
import {
  ServiceEmailRequest,
  ServiceEmailRequestSubmitData,
  ServiceEmailSenderOption,
  ServiceEmailStatus,
} from "../../models/ServiceEmailRequestModel";
import { ServiceEmailSolicitud } from "../../models/ServiceEmailSolicitudModel";
import useServiceEmailsService from "../../services/useServiceEmailsService";
import useServiceEmailSolicitudesService from "../../services/useServiceEmailSolicitudesService";
import useCompaniesService from "../../services/useCompaniesService";
import { useSessionStorageState, useLocalStorageState } from "../../hooks/useSessionStorageState";
import {
  MonitorMode,
  MonitorNotice,
  appendMonitorNotices,
  buildActivityFromSolicitudes,
  buildMonitorAlerts,
  computeMonitorStats,
  formatDayLabel as formatMonitorDayLabel,
  noticesFromActivity,
  noticesFromAlerts,
  noticesFromSyncNotifications,
  todayIso as monitorTodayIso,
} from "../../components/Services/tables/emailsMonitorUtils";
import EndPoints from "../../config/EndPoint";
import { ServiceEmailNotification } from "../../models/ServiceEmailNotificationModel";
import { canExportEmailsAndServicesExcel } from "../../hooks/usePermissions";
import "../../components/Services/tables/styles/ServiceEmailSolicitudesTable.css";
import "../../components/Services/tables/styles/EmailsMonitorSidePanel.css";

const EMAILS_FILTERS_KEY = "emails.list.filters";
const EMAILS_SOLICITUD_FILTERS_KEY = "emails.solicitudes.filters";
const EMAILS_TAB_KEY = "emails.list.activeTab";
const EMAILS_MODE_KEY = "emails.solicitudes.monitorMode";
const EMAILS_PAGINATION_KEY = "emails.list.pagination";
const EMAILS_SOLICITUD_PAGINATION_KEY = "emails.solicitudes.pagination";
const EMAILS_MONITOR_NOTICES_KEY = "emails.monitor.notices";
const EMAILS_MONITOR_COLLAPSED_KEY = "emails.monitor.collapsed";
const EMAILS_VIEW_MODE_KEY = "emails.list.viewMode";

type EmailsPageTab = "emails" | "solicitudes";
type EmailsViewMode = "detalle" | "simple";

type EmailKpiBucket = {
  correos: number;
  pendientes: number;
  agregados: number;
  solicitudes_sin_servicio: number;
};

type EmailKpis = {
  as_of_date: string;
  service_date: string;
  is_today: boolean;
  is_service_today: boolean;
  day: EmailKpiBucket;
  general: EmailKpiBucket;
};

const emptyBucket = (): EmailKpiBucket => ({
  correos: 0,
  pendientes: 0,
  agregados: 0,
  solicitudes_sin_servicio: 0,
});

const emptyKpis = (): EmailKpis => ({
  as_of_date: "",
  service_date: "",
  is_today: true,
  is_service_today: true,
  day: emptyBucket(),
  general: emptyBucket(),
});

const todayIso = monitorTodayIso;

const formatDayLabel = (iso: string, isToday: boolean) => {
  if (!iso) return isToday ? "Hoy" : "Día";
  if (isToday) return "Hoy";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y.slice(2)}`;
};

const emptyEmailFilters = (): ServiceEmailsTableFilters => ({
  search: "",
  status: "",
  received_date: todayIso(),
  request_date: "",
  company_id: "",
  service_id: "",
});

const emptySolicitudFilters = (): ServiceEmailSolicitudesTableFilters => ({
  search: "",
  email_status: "",
  service_date: todayIso(),
  pending_only: false,
  missing_driver_only: false,
  origin: "",
  destination: "",
  passengers: "",
  time_from: "",
  time_to: "",
});

/** Normaliza filtros viejos (date_from/date_to) a un solo día. */
const normalizeSolicitudFilters = (
  raw: ServiceEmailSolicitudesTableFilters & {
    date_from?: string;
    date_to?: string;
  }
): ServiceEmailSolicitudesTableFilters => {
  const legacyDay = (raw.date_from || raw.date_to || "").trim();
  const service_date = (raw.service_date || legacyDay || todayIso()).trim() || todayIso();
  return {
    search: raw.search || "",
    email_status: raw.email_status || "",
    service_date,
    pending_only: Boolean(raw.pending_only),
    missing_driver_only: Boolean(raw.missing_driver_only),
    origin: raw.origin || "",
    destination: raw.destination || "",
    passengers: raw.passengers || "",
    time_from: raw.time_from || "",
    time_to: raw.time_to || "",
  };
};

const SOLICITUDES_POLL_MS = 30_000;

const EmailsPage: React.FC = () => {
  const navigate = useNavigate();
  const serviceEmailSvc = useServiceEmailsService();
  const { listAll: listAllSolicitudes } = useServiceEmailSolicitudesService();
  const { list: listCompanies } = useCompaniesService();
  const {
    list: listServiceEmails,
    kpis: loadEmailKpis,
    store: storeServiceEmail,
    update: updateServiceEmail,
    updateStatus: updateServiceEmailStatus,
    updateReceivedAt: updateServiceEmailReceivedAt,
    senderOptions: loadServiceEmailSenderOptions,
    fetching,
  } = serviceEmailSvc;

  const [activeTab, setActiveTab] = useSessionStorageState<EmailsPageTab>(EMAILS_TAB_KEY, "emails");
  const [emailsViewMode, setEmailsViewMode] = useSessionStorageState<EmailsViewMode>(
    EMAILS_VIEW_MODE_KEY,
    "detalle"
  );
  const [monitorMode, setMonitorMode] = useSessionStorageState<MonitorMode>(
    EMAILS_MODE_KEY,
    "servicio"
  );
  const [noticeHistory, setNoticeHistory] = useLocalStorageState<MonitorNotice[]>(
    EMAILS_MONITOR_NOTICES_KEY,
    []
  );
  const [monitorCollapsed, setMonitorCollapsed] = useLocalStorageState<boolean>(
    EMAILS_MONITOR_COLLAPSED_KEY,
    false
  );
  const [serviceEmails, setServiceEmails] = useState<ServiceEmailRequest[]>([]);
  const [solicitudes, setSolicitudes] = useState<ServiceEmailSolicitud[]>([]);
  const [loadingServiceEmails, setLoadingServiceEmails] = useState(false);
  const [loadingSolicitudes, setLoadingSolicitudes] = useState(false);
  const [solicitudesUpdatedAt, setSolicitudesUpdatedAt] = useState<Date | null>(null);
  const [kpis, setKpis] = useState<EmailKpis>(emptyKpis);
  const [loadingKpis, setLoadingKpis] = useState(true);
  const [pagination, setPagination] = useSessionStorageState(EMAILS_PAGINATION_KEY, {
    page: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 20,
    hasNext: false,
    hasPrevious: false,
  });
  const [solicitudPagination, setSolicitudPagination] = useSessionStorageState(
    EMAILS_SOLICITUD_PAGINATION_KEY,
    {
      page: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 20,
      hasNext: false,
      hasPrevious: false,
    }
  );
  const [filters, setFilters] = useSessionStorageState<ServiceEmailsTableFilters>(
    EMAILS_FILTERS_KEY,
    emptyEmailFilters()
  );

  // Si no hay fecha Llegó (sesión antigua o limpia), KPIs y listado usan el día actual
  useEffect(() => {
    setFilters((prev) => {
      if ((prev.received_date ?? "").trim()) return prev;
      return { ...prev, received_date: todayIso() };
    });
  }, [setFilters]);
  const [solicitudFilters, setSolicitudFilters] =
    useSessionStorageState<ServiceEmailSolicitudesTableFilters>(
      EMAILS_SOLICITUD_FILTERS_KEY,
      emptySolicitudFilters()
    );

  useEffect(() => {
    setSolicitudFilters((prev) => {
      const next = normalizeSolicitudFilters(prev as any);
      if (
        next.service_date === prev.service_date &&
        next.search === prev.search &&
        next.email_status === prev.email_status &&
        Boolean(next.pending_only) === Boolean(prev.pending_only) &&
        Boolean(next.missing_driver_only) === Boolean(prev.missing_driver_only) &&
        !("date_from" in (prev as object)) &&
        !("date_to" in (prev as object))
      ) {
        return prev;
      }
      return next;
    });
  }, [setSolicitudFilters]);
  const [companies, setCompanies] = useState<Array<{ value: string; label: string }>>([]);
  const [companyFilter, setCompanyFilter] = useState("");
  const [senderOptions, setSenderOptions] = useState<ServiceEmailSenderOption[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const canExportExcel = canExportEmailsAndServicesExcel();
  const [savingReceivedAtId, setSavingReceivedAtId] = useState<number | null>(null);
  const [commentTarget, setCommentTarget] = useState<ServiceEmailRequest | null>(null);

  const activeRequestRef = useRef<AbortController | null>(null);
  const solicitudesRequestRef = useRef<AbortController | null>(null);
  const senderOptionsRequestRef = useRef<AbortController | null>(null);
  const companiesRequestRef = useRef<AbortController | null>(null);
  const kpisRequestRef = useRef<AbortController | null>(null);
  const paginationRef = useRef(pagination);
  const solicitudPaginationRef = useRef(solicitudPagination);
  const filtersRef = useRef(filters);
  const solicitudFiltersRef = useRef(solicitudFilters);
  // Primera carga (o remount al volver de editar): respetar página persistida
  const emailsInitialLoadRef = useRef(true);
  const solicitudesInitialLoadRef = useRef(true);
  const prevEmailFiltersRef = useRef(filters);
  const prevSolicitudFiltersRef = useRef(solicitudFilters);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    solicitudPaginationRef.current = solicitudPagination;
  }, [solicitudPagination]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    solicitudFiltersRef.current = solicitudFilters;
  }, [solicitudFilters]);

  const loadKpis = useCallback(
    (receivedDate?: string, requestDate?: string) => {
      kpisRequestRef.current?.abort();
      const ctrl = new AbortController();
      kpisRequestRef.current = ctrl;
      setLoadingKpis(true);

      const date = (receivedDate ?? "").trim() || todayIso();
      const serviceDate = (requestDate ?? "").trim();
      const kpiParams: Record<string, string> = { received_date: date };
      if (serviceDate) kpiParams.request_date = serviceDate;

      loadEmailKpis(
        kpiParams,
        {
          onSuccess: (resp: any) => {
            const data = resp?.data ?? {};
            const day = data.day ?? {};
            const general = data.general ?? {};
            setKpis({
              as_of_date: String(data.as_of_date || date),
              service_date: String(data.service_date || serviceDate || date),
              is_today: Boolean(data.is_today ?? true),
              is_service_today: Boolean(data.is_service_today ?? data.is_today ?? true),
              day: {
                correos: Number(day.correos ?? data.correos_hoy) || 0,
                pendientes: Number(day.pendientes ?? data.correos_pendientes) || 0,
                agregados: Number(day.agregados ?? data.correos_agregados) || 0,
                solicitudes_sin_servicio:
                  Number(day.solicitudes_sin_servicio ?? data.solicitudes_sin_servicio) || 0,
              },
              general: {
                correos: Number(general.correos) || 0,
                pendientes: Number(general.pendientes) || 0,
                agregados: Number(general.agregados) || 0,
                solicitudes_sin_servicio: Number(general.solicitudes_sin_servicio) || 0,
              },
            });
            setLoadingKpis(false);
          },
          onError: () => {
            if (ctrl.signal.aborted) return;
            setLoadingKpis(false);
          },
        },
        ctrl.signal
      );
    },
    [loadEmailKpis]
  );

  const loadEmails = useCallback(
    (
      page: number = 1,
      currentFilters: ServiceEmailsTableFilters = filtersRef.current,
      pageSizeParam?: number
    ) => {
      setLoadingServiceEmails(true);
      activeRequestRef.current?.abort();
      const ctrl = new AbortController();
      activeRequestRef.current = ctrl;

      const requestedPageSize = Math.min(
        MAX_PAGE_SIZE,
        pageSizeParam !== undefined ? pageSizeParam : paginationRef.current.pageSize
      );

      if (pageSizeParam !== undefined) {
        setPagination((prev) => ({
          ...prev,
          page: 1,
          pageSize: requestedPageSize,
        }));
      }

      const apiFilters: Record<string, string | number> = {
        page,
        page_size: requestedPageSize,
      };
      if (currentFilters.search.trim()) apiFilters.search = currentFilters.search.trim();
      if (currentFilters.status) apiFilters.status = currentFilters.status;
      if (currentFilters.received_date) apiFilters.received_date = currentFilters.received_date;
      if (currentFilters.request_date) apiFilters.request_date = currentFilters.request_date;
      if (currentFilters.company_id) apiFilters.company_id = currentFilters.company_id;
      if (currentFilters.service_id?.trim()) apiFilters.service_id = currentFilters.service_id.trim();

      listServiceEmails(
        apiFilters,
        {
          onSuccess: (resp: any) => {
            const data = resp?.data ?? {};
            const items = Array.isArray(data.service_emails) ? data.service_emails : [];
            const p = data.pagination ?? {};
            const totalCount = p.total_count ?? items.length;
            const pageSize = Math.min(MAX_PAGE_SIZE, Number(p.page_size) || requestedPageSize);
            const currentPage = Number(p.page) || page;
            const totalPages = totalCount > 0 ? Math.ceil(totalCount / Math.max(pageSize, 1)) : 1;

            setServiceEmails(items);
            setPagination({
              page: currentPage,
              totalPages,
              totalCount,
              pageSize,
              hasNext: currentPage < totalPages,
              hasPrevious: currentPage > 1,
            });
            setLoadingServiceEmails(false);
          },
          onError: () => {
            setLoadingServiceEmails(false);
            showToast("Error cargando correos", "error");
          },
        },
        ctrl.signal
      );
    },
    [listServiceEmails]
  );

  const loadSolicitudes = useCallback(
    (
      page: number = 1,
      currentFilters: ServiceEmailSolicitudesTableFilters = solicitudFiltersRef.current,
      pageSizeParam?: number
    ) => {
      setLoadingSolicitudes(true);
      solicitudesRequestRef.current?.abort();
      const ctrl = new AbortController();
      solicitudesRequestRef.current = ctrl;

      const requestedPageSize = Math.min(
        MAX_PAGE_SIZE,
        pageSizeParam !== undefined ? pageSizeParam : solicitudPaginationRef.current.pageSize
      );

      if (pageSizeParam !== undefined) {
        setSolicitudPagination((prev) => ({
          ...prev,
          page: 1,
          pageSize: requestedPageSize,
        }));
      }

      const apiFilters: Record<string, string | number> = {
        page,
        page_size: requestedPageSize,
      };
      if (currentFilters.search.trim()) apiFilters.search = currentFilters.search.trim();
      if (currentFilters.email_status) apiFilters.email_status = currentFilters.email_status;
      const day = (currentFilters.service_date || todayIso()).trim() || todayIso();
      apiFilters.date_from = day;
      apiFilters.date_to = day;
      
      // Filtros específicos
      if (currentFilters.origin?.trim()) apiFilters.origin = currentFilters.origin.trim();
      if (currentFilters.destination?.trim()) apiFilters.destination = currentFilters.destination.trim();
      if (currentFilters.passengers?.trim()) apiFilters.passengers = currentFilters.passengers.trim();
      if (currentFilters.time_from?.trim()) apiFilters.time_from = currentFilters.time_from.trim();
      if (currentFilters.time_to?.trim()) apiFilters.time_to = currentFilters.time_to.trim();

      listAllSolicitudes(
        apiFilters,
        {
          onSuccess: (resp: any) => {
            const data = resp?.data ?? {};
            const items = Array.isArray(data.solicitudes) ? data.solicitudes : [];
            const p = data.pagination ?? {};
            const totalCount = p.total_count ?? items.length;
            const pageSize = Math.min(MAX_PAGE_SIZE, Number(p.page_size) || requestedPageSize);
            const currentPage = Number(p.page) || page;
            const totalPages = totalCount > 0 ? Math.ceil(totalCount / Math.max(pageSize, 1)) : 1;

            setSolicitudes(items);
            setSolicitudPagination({
              page: currentPage,
              totalPages,
              totalCount,
              pageSize,
              hasNext: currentPage < totalPages,
              hasPrevious: currentPage > 1,
            });
            setSolicitudesUpdatedAt(new Date());
            setLoadingSolicitudes(false);
          },
          onError: () => {
            setLoadingSolicitudes(false);
            showToast("Error cargando solicitudes", "error");
          },
        },
        ctrl.signal
      );
    },
    [listAllSolicitudes]
  );

  // Mantener datos del panel lateral con el mismo día de filtros
  useEffect(() => {
    if (activeTab === "solicitudes") return;
    const ctrl = new AbortController();
    const day =
      (filters.request_date || filters.received_date || todayIso()).trim() || todayIso();
    listAllSolicitudes(
      {
        page: 1,
        page_size: Math.min(MAX_PAGE_SIZE, 100),
        date_from: day,
        date_to: day,
      },
      {
        onSuccess: (resp: any) => {
          if (ctrl.signal.aborted) return;
          const data = resp?.data ?? {};
          const items = Array.isArray(data.solicitudes) ? data.solicitudes : [];
          setSolicitudes(items);
          setSolicitudesUpdatedAt(new Date());
        },
        onError: () => {
          /* el panel reintenta al cambiar de tab */
        },
      },
      ctrl.signal
    );
    return () => ctrl.abort();
  }, [
    activeTab,
    filters.received_date,
    filters.request_date,
    listAllSolicitudes,
  ]);

  // Un solo día operativo: prioriza Día del servicio, si no Llegó el correo.
  // Sync unidireccional correos → solicitudes para evitar loop infinito.
  useEffect(() => {
    const serviceDay = (filters.request_date || filters.received_date || "").trim();
    if (!serviceDay) return;
    setSolicitudFilters((prev) => {
      if (prev.service_date === serviceDay) return prev;
      return { ...prev, service_date: serviceDay };
    });
  }, [filters.received_date, filters.request_date, setSolicitudFilters]);

  const handleSolicitudFiltersChange = useCallback(
    (next: ServiceEmailSolicitudesTableFilters) => {
      const normalized = normalizeSolicitudFilters(next as any);
      setSolicitudFilters(normalized);
      const day = (normalized.service_date || "").trim();
      if (!day) return;
      setFilters((prev) => {
        if (prev.request_date === day) return prev;
        return { ...prev, request_date: day };
      });
    },
    [setFilters, setSolicitudFilters],
  );

  useEffect(() => {
    if (activeTab !== "solicitudes") return;
    const intervalId = window.setInterval(() => {
      loadSolicitudes(
        solicitudPaginationRef.current.page,
        solicitudFiltersRef.current,
        solicitudPaginationRef.current.pageSize
      );
    }, SOLICITUDES_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [activeTab, loadSolicitudes]);

  // KPIs del día en paralelo al abrir / al cambiar fechas
  useEffect(() => {
    loadKpis(filters.received_date, filters.request_date);
    return () => {
      kpisRequestRef.current?.abort();
    };
  }, [filters.received_date, filters.request_date, loadKpis]);

  useEffect(() => {
    if (activeTab !== "emails") return;
    const filtersChanged = prevEmailFiltersRef.current !== filters;
    prevEmailFiltersRef.current = filters;
    const delay = filters.search.trim() ? 400 : 0;
    const timeoutId = setTimeout(() => {
      const pageToLoad =
        emailsInitialLoadRef.current || !filtersChanged
          ? paginationRef.current.page || 1
          : 1;
      emailsInitialLoadRef.current = false;
      loadEmails(pageToLoad, filters);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      activeRequestRef.current?.abort();
      setLoadingServiceEmails(false);
    };
  }, [activeTab, filters, loadEmails]);

  useEffect(() => {
    if (activeTab !== "solicitudes") return;
    const filtersChanged = prevSolicitudFiltersRef.current !== solicitudFilters;
    prevSolicitudFiltersRef.current = solicitudFilters;
    const delay = solicitudFilters.search.trim() ? 400 : 0;
    const timeoutId = setTimeout(() => {
      const pageToLoad =
        solicitudesInitialLoadRef.current || !filtersChanged
          ? solicitudPaginationRef.current.page || 1
          : 1;
      solicitudesInitialLoadRef.current = false;
      loadSolicitudes(pageToLoad, solicitudFilters);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      solicitudesRequestRef.current?.abort();
      setLoadingSolicitudes(false);
    };
  }, [activeTab, solicitudFilters, loadSolicitudes]);

  const loadSenderOptionsForCompany = useCallback(
    (companyId: string) => {
      senderOptionsRequestRef.current?.abort();
      setSenderOptions([]);

      if (!companyId) return;

      const ctrl = new AbortController();
      senderOptionsRequestRef.current = ctrl;

      loadServiceEmailSenderOptions(
        { company_id: companyId },
        {
          onSuccess: (resp: any) => {
            const data = resp?.data ?? {};
            setSenderOptions(Array.isArray(data.sender_options) ? data.sender_options : []);
          },
          onError: () => {
            if (ctrl.signal.aborted) return;
            showToast("Error cargando emisores de correo", "error");
          },
        },
        ctrl.signal
      );
    },
    [loadServiceEmailSenderOptions]
  );

  useEffect(() => {
    companiesRequestRef.current?.abort();
    const ctrl = new AbortController();
    companiesRequestRef.current = ctrl;

    listCompanies(
      {},
      {
        onSuccess: (resp: any) => {
          const items = Array.isArray(resp?.data?.companies) ? resp.data.companies : [];
          setCompanies(
            items.map((company: any) => ({
              value: String(company.id),
              label: company.name ?? company.company_name ?? "Sin nombre",
            }))
          );
        },
        onError: () => {
          if (ctrl.signal.aborted) return;
          showToast("Error cargando empresas", "error");
        },
      },
      ctrl.signal
    );

    return () => {
      ctrl.abort();
      if (companiesRequestRef.current === ctrl) {
        companiesRequestRef.current = null;
      }
    };
  }, [listCompanies]);

  useEffect(() => {
    if (showForm) return;
    setCompanyFilter("");
    setSenderOptions([]);
    senderOptionsRequestRef.current?.abort();
  }, [showForm]);

  const handleCompanyFilterChange = useCallback(
    (companyId: string) => {
      setCompanyFilter(companyId);
      loadSenderOptionsForCompany(companyId);
    },
    [loadSenderOptionsForCompany]
  );

  const closeForm = useCallback(() => {
    setShowForm(false);
    setCompanyFilter("");
  }, []);

  const refreshActiveList = useCallback(() => {
    loadKpis(filtersRef.current.received_date, filtersRef.current.request_date);
    if (activeTab === "solicitudes") {
      loadSolicitudes(
        solicitudPaginationRef.current.page,
        solicitudFiltersRef.current,
        solicitudPaginationRef.current.pageSize
      );
      return;
    }
    loadEmails(paginationRef.current.page, filtersRef.current, paginationRef.current.pageSize);
  }, [activeTab, loadEmails, loadKpis, loadSolicitudes]);

  const handleSubmit = useCallback(
    async (
      data: ServiceEmailRequestSubmitData | ServiceEmailRequestSubmitData[],
      item?: ServiceEmailRequest | null
    ) => {
      const batch = Array.isArray(data) ? data : [data];

      if (!item?.id) {
        let created = 0;
        for (const entry of batch) {
          const resp: any = await storeServiceEmail({
            email: entry.email,
            solicitudes: entry.solicitudes,
          });
          const failed =
            !resp ||
            resp?.success === false ||
            (typeof resp?.http_status === "number" && resp.http_status >= 400);

          if (failed) {
            showToast(
              resp?.message ||
                `No se pudo guardar el correo${created ? ` (${created} creados antes del error)` : ""}`,
              "error"
            );
            if (created > 0) {
              closeForm();
              refreshActiveList();
            }
            return;
          }
          created += 1;
        }

        showToast(
          created > 1
            ? `${created} correos creados correctamente`
            : "Correo y solicitudes creados correctamente",
          "success"
        );
        closeForm();
        refreshActiveList();
        return;
      }

      const email = batch[0]?.email;
      if (!email) return;
      const resp: any = await updateServiceEmail(item.id, email);
      const failed =
        !resp ||
        resp?.success === false ||
        (typeof resp?.http_status === "number" && resp.http_status >= 400);

      if (failed) {
        showToast(resp?.message || "No se pudo guardar el correo", "error");
        return;
      }

      showToast("Correo actualizado correctamente", "success");
      closeForm();
      refreshActiveList();
    },
    [closeForm, refreshActiveList, storeServiceEmail, updateServiceEmail]
  );

  const handleStatusChange = useCallback(
    async (item: ServiceEmailRequest, status: ServiceEmailStatus) => {
      const resp: any = await updateServiceEmailStatus(item.id, status, {
        service: item.service_id ?? item.service ?? null,
      });
      const failed =
        !resp ||
        resp?.success === false ||
        (typeof resp?.http_status === "number" && resp.http_status >= 400);

      if (failed) {
        showToast(resp?.message || "No se pudo cambiar el estado del correo", "error");
        return;
      }

      showToast(
        status === "AGREGADO" ? "Correo marcado como agregado" : "Correo marcado como pendiente",
        "success",
      );
      refreshActiveList();
    },
    [refreshActiveList, updateServiceEmailStatus],
  );

  const handleReceivedTimeChange = useCallback(
    async (item: ServiceEmailRequest, time: string) => {
      setSavingReceivedAtId(item.id);
      try {
        const resp: any = await updateServiceEmailReceivedAt(item.id, { time });
        const failed =
          !resp ||
          resp?.success === false ||
          (typeof resp?.http_status === "number" && resp.http_status >= 400);

        if (failed) {
          showToast(resp?.message || "No se pudo actualizar la hora", "error");
          return;
        }

        const updated = resp?.data as ServiceEmailRequest | undefined;
        if (updated?.received_at) {
          setServiceEmails((prev) =>
            prev.map((row) =>
              row.id === item.id ? { ...row, received_at: updated.received_at } : row
            )
          );
        } else {
          refreshActiveList();
        }
        showToast("Hora de llegada actualizada", "success");
      } finally {
        setSavingReceivedAtId(null);
      }
    },
    [refreshActiveList, updateServiceEmailReceivedAt]
  );

  const applyKpiFilter = (kind: "sin-servicio" | "hoy" | "pendientes" | "agregados") => {
    const receivedDay = filters.received_date || todayIso();
    const serviceDay = filters.request_date || receivedDay;
    if (kind === "sin-servicio") {
      setActiveTab("solicitudes");
      setMonitorMode("servicio");
      setSolicitudFilters((prev) => ({
        ...prev,
        email_status: "",
        service_date: serviceDay,
        pending_only: true,
        missing_driver_only: false,
      }));
      return;
    }
    setActiveTab("emails");
    if (kind === "hoy") {
      setFilters((prev) => ({ ...prev, status: "", received_date: receivedDay }));
      return;
    }
    if (kind === "pendientes") {
      setFilters((prev) => ({ ...prev, status: "PENDIENTE", received_date: receivedDay }));
      return;
    }
    setFilters((prev) => ({ ...prev, status: "AGREGADO", received_date: receivedDay }));
  };

  const focusMissingService = useCallback(() => {
    const day =
      (
        solicitudFilters.service_date ||
        filters.request_date ||
        filters.received_date ||
        todayIso()
      ).trim() || todayIso();
    setActiveTab("solicitudes");
    setMonitorMode("servicio");
    setSolicitudFilters((prev) => ({
      ...prev,
      service_date: day,
      pending_only: true,
      missing_driver_only: false,
      email_status: "",
    }));
  }, [
    filters.received_date,
    filters.request_date,
    solicitudFilters.service_date,
    setActiveTab,
    setMonitorMode,
    setSolicitudFilters,
  ]);

  const focusMissingDriver = useCallback(() => {
    const day =
      (
        solicitudFilters.service_date ||
        filters.request_date ||
        filters.received_date ||
        todayIso()
      ).trim() || todayIso();
    setActiveTab("solicitudes");
    setMonitorMode("conductor");
    setSolicitudFilters((prev) => ({
      ...prev,
      service_date: day,
      pending_only: false,
      missing_driver_only: true,
      email_status: "",
    }));
  }, [
    filters.received_date,
    filters.request_date,
    solicitudFilters.service_date,
    setActiveTab,
    setMonitorMode,
    setSolicitudFilters,
  ]);

  const opsDay =
    (
      solicitudFilters.service_date ||
      filters.request_date ||
      filters.received_date ||
      todayIso()
    ).trim() || todayIso();
  const monitorDayLabel = formatMonitorDayLabel(opsDay);
  const kpiMatchesOpsDay =
    opsDay === (kpis.service_date || filters.request_date || filters.received_date || "");

  const monitorStats = useMemo(() => computeMonitorStats(solicitudes), [solicitudes]);
  const monitorAlerts = useMemo(
    () =>
      buildMonitorAlerts(monitorStats, {
        dayLabel: monitorDayLabel,
        kpiSinServicio: kpiMatchesOpsDay ? kpis.day.solicitudes_sin_servicio : undefined,
      }),
    [monitorStats, monitorDayLabel, kpiMatchesOpsDay, kpis.day.solicitudes_sin_servicio]
  );
  const monitorActivity = useMemo(
    () => buildActivityFromSolicitudes(solicitudes),
    [solicitudes]
  );

  const getAccessToken = useCallback((): string | null => {
    try {
      const token =
        localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      if (!token || token === "null" || token === "undefined") return null;
      return token;
    } catch {
      return null;
    }
  }, []);

  const loadSyncNotifications = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    try {
      const res = await fetch(EndPoints.SERVICES.EMAILS_NOTIFICATIONS, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) return;
      const body = await res.json().catch(() => null);
      const items = Array.isArray(body?.data?.notifications)
        ? (body.data.notifications as ServiceEmailNotification[])
        : [];
      setNoticeHistory((prev) =>
        appendMonitorNotices(prev, noticesFromSyncNotifications(items))
      );
    } catch {
      /* el siguiente ciclo reintenta */
    }
  }, [getAccessToken, setNoticeHistory]);

  useEffect(() => {
    void loadSyncNotifications();
    const intervalId = window.setInterval(() => {
      void loadSyncNotifications();
    }, 60_000);
    return () => window.clearInterval(intervalId);
  }, [loadSyncNotifications]);

  useEffect(() => {
    const incoming = [
      ...noticesFromAlerts(monitorAlerts),
      ...noticesFromActivity(monitorActivity.filter((item) => item.tone !== "success")),
    ];
    setNoticeHistory((prev) => appendMonitorNotices(prev, incoming));
  }, [monitorAlerts, monitorActivity, setNoticeHistory]);

  const kpiLoadingClass = loadingKpis ? " is-loading" : "";
  const kpiDayLabel = formatDayLabel(kpis.as_of_date || filters.received_date || todayIso(), kpis.is_today);
  const kpiServiceDayLabel = formatDayLabel(
    kpis.service_date || filters.request_date || filters.received_date || todayIso(),
    kpis.is_service_today
  );

  const refreshMonitor = useCallback(() => {
    loadKpis(filtersRef.current.received_date, filtersRef.current.request_date);
    loadSolicitudes(
      solicitudPaginationRef.current.page,
      solicitudFiltersRef.current,
      solicitudPaginationRef.current.pageSize
    );
    void loadSyncNotifications();
  }, [loadKpis, loadSolicitudes, loadSyncNotifications]);

  return (
    <div className="services-page">
      <header className="services-header">
        <h1>Correos</h1>
        <p>Gestión de correos recibidos y monitoreo de solicitudes / servicios</p>
      </header>

      <div className="services-header-actions">
        {canExportExcel && (
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowExportModal(true)}
          >
            Descargar Excel
          </button>
        )}
        <button className="btn-secondary" onClick={() => setShowBulkForm(true)}>
          Carga masiva
        </button>
        <button className="btn-create" onClick={() => setShowForm(true)}>
          + Nuevo Correo
        </button>
      </div>

      <section className="emails-kpi-grid" aria-label="Indicadores de correos">
        <div className="emails-kpi-day-banner">
          <span className="emails-kpi-day-title">KPIs del día</span>
          <span className="emails-kpi-day-value">{kpiDayLabel}</span>
          <span className="emails-kpi-day-hint">
            {filters.received_date === todayIso()
              ? "Día actual"
              : filters.received_date
                ? "Según filtro Llegó"
                : "Sin filtro Llegó → se usa el día de hoy"}
          </span>
        </div>

        <button
          type="button"
          className={`emails-kpi-card emails-kpi-card--danger${kpiLoadingClass}${
            activeTab === "solicitudes" ? " is-active" : ""
          }`}
          onClick={() => applyKpiFilter("sin-servicio")}
        >
          <span className="emails-kpi-label">Solicitudes Sin servicio</span>
          <span className="emails-kpi-scope">Día servicio · {kpiServiceDayLabel}</span>
          <span className="emails-kpi-value">
            {loadingKpis ? "—" : kpis.day.solicitudes_sin_servicio}
          </span>
          <span className="emails-kpi-hint">
            General: {loadingKpis ? "—" : kpis.general.solicitudes_sin_servicio}
          </span>
        </button>
        <button
          type="button"
          className={`emails-kpi-card emails-kpi-card--info${kpiLoadingClass}${
            activeTab === "emails" && !!filters.received_date && !filters.status ? " is-active" : ""
          }`}
          onClick={() => applyKpiFilter("hoy")}
        >
          <span className="emails-kpi-label">Correos Recibidos</span>
          <span className="emails-kpi-scope">Día · {kpiDayLabel}</span>
          <span className="emails-kpi-value">{loadingKpis ? "—" : kpis.day.correos}</span>
          <span className="emails-kpi-hint">
            General: {loadingKpis ? "—" : kpis.general.correos}
          </span>
        </button>
        <button
          type="button"
          className={`emails-kpi-card emails-kpi-card--warning${kpiLoadingClass}${
            filters.status === "PENDIENTE" ? " is-active" : ""
          }`}
          onClick={() => applyKpiFilter("pendientes")}
        >
          <span className="emails-kpi-label">Pendientes</span>
          <span className="emails-kpi-scope">Día · {kpiDayLabel}</span>
          <span className="emails-kpi-value">{loadingKpis ? "—" : kpis.day.pendientes}</span>
          <span className="emails-kpi-hint">
            General: {loadingKpis ? "—" : kpis.general.pendientes}
          </span>
        </button>
        <button
          type="button"
          className={`emails-kpi-card emails-kpi-card--success${kpiLoadingClass}${
            filters.status === "AGREGADO" ? " is-active" : ""
          }`}
          onClick={() => applyKpiFilter("agregados")}
        >
          <span className="emails-kpi-label">Agregados</span>
          <span className="emails-kpi-scope">Día · {kpiDayLabel}</span>
          <span className="emails-kpi-value">{loadingKpis ? "—" : kpis.day.agregados}</span>
          <span className="emails-kpi-hint">
            General: {loadingKpis ? "—" : kpis.general.agregados}
          </span>
        </button>
      </section>

      <div
        className={`emails-workspace${monitorCollapsed ? " emails-workspace--panel-collapsed" : ""}`}
      >
        <div className="emails-workspace-main">
          <div className="emails-page-tabs" role="tablist" aria-label="Vistas de correos">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "emails"}
              className={`emails-page-tab${activeTab === "emails" ? " is-active" : ""}`}
              onClick={() => setActiveTab("emails")}
            >
              Correos
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "solicitudes"}
              className={`emails-page-tab${activeTab === "solicitudes" ? " is-active" : ""}`}
              onClick={() => setActiveTab("solicitudes")}
            >
              Solicitudes
            </button>
            {activeTab === "emails" && (
              <div className="emails-view-toggle" role="group" aria-label="Modo de vista de correos">
                <button
                  type="button"
                  className={`emails-view-toggle__btn${
                    emailsViewMode === "detalle" ? " is-active" : ""
                  }`}
                  onClick={() => setEmailsViewMode("detalle")}
                >
                  Detalle
                </button>
                <button
                  type="button"
                  className={`emails-view-toggle__btn${
                    emailsViewMode === "simple" ? " is-active" : ""
                  }`}
                  onClick={() => setEmailsViewMode("simple")}
                >
                  Vista simple
                </button>
              </div>
            )}
          </div>

          <section className="services-list">
            {activeTab === "emails" ? (
              <>
                {emailsViewMode === "simple" ? (
                  <ServiceEmailsSimpleTable
                    data={serviceEmails}
                    loading={fetching.list || loadingServiceEmails}
                    savingTimeId={savingReceivedAtId}
                    onOpen={(item) =>
                      navigate(`/emails/edit/${item.id}`, { state: { email: item } })
                    }
                    onReceivedTimeChange={handleReceivedTimeChange}
                  />
                ) : (
                  <ServiceEmailsTable
                    data={serviceEmails}
                    totalCount={pagination.totalCount}
                    loading={fetching.list || loadingServiceEmails}
                    filters={filters}
                    companies={companies}
                    onFiltersChange={setFilters}
                    onEdit={(item) =>
                      navigate(`/emails/edit/${item.id}`, { state: { email: item } })
                    }
                    onStatusChange={handleStatusChange}
                    onComment={setCommentTarget}
                  />
                )}
                {pagination.totalCount > 0 && (
                  <Pagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    totalCount={pagination.totalCount}
                    pageSize={pagination.pageSize}
                    onPageChange={(page) => {
                      loadEmails(page, filtersRef.current);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onPageSizeChange={(newSize) => {
                      loadEmails(1, filtersRef.current, newSize);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  />
                )}
              </>
            ) : (
              <>
                <ServiceEmailSolicitudesTable
                  data={solicitudes}
                  totalCount={solicitudPagination.totalCount}
                  loading={Boolean(fetching.listAll) || loadingSolicitudes}
                  filters={solicitudFilters}
                  mode={monitorMode}
                  lastUpdatedAt={solicitudesUpdatedAt}
                  live
                  dayLabel={monitorDayLabel}
                  kpiSinServicio={
                    kpiMatchesOpsDay ? kpis.day.solicitudes_sin_servicio : undefined
                  }
                  onModeChange={setMonitorMode}
                  onRefresh={() =>
                    loadSolicitudes(
                      solicitudPaginationRef.current.page,
                      solicitudFiltersRef.current,
                      solicitudPaginationRef.current.pageSize
                    )
                  }
                  onFiltersChange={handleSolicitudFiltersChange}
                  onOpenEmail={(emailId) => navigate(`/emails/edit/${emailId}`)}
                />
                {solicitudPagination.totalCount > 0 && (
                  <Pagination
                    page={solicitudPagination.page}
                    totalPages={solicitudPagination.totalPages}
                    totalCount={solicitudPagination.totalCount}
                    pageSize={solicitudPagination.pageSize}
                    onPageChange={(page) => {
                      loadSolicitudes(page, solicitudFiltersRef.current);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onPageSizeChange={(newSize) => {
                      loadSolicitudes(1, solicitudFiltersRef.current, newSize);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  />
                )}
              </>
            )}
          </section>
        </div>

        <EmailsMonitorSidePanel
          mode={monitorMode}
          stats={monitorStats}
          alerts={monitorAlerts}
          notices={noticeHistory}
          dayLabel={monitorDayLabel}
          lastUpdatedAt={solicitudesUpdatedAt}
          live={activeTab === "solicitudes"}
          loading={Boolean(fetching.listAll) || loadingSolicitudes}
          collapsed={monitorCollapsed}
          onCollapsedChange={setMonitorCollapsed}
          onModeChange={(mode) => {
            setMonitorMode(mode);
            setActiveTab("solicitudes");
          }}
          onFocusMissingService={focusMissingService}
          onFocusMissingDriver={focusMissingDriver}
          onOpenNotice={(notice) => {
            if (notice.serviceId) {
              navigate(`/services/edit/${notice.serviceId}`);
              return;
            }
            if (notice.emailId) {
              navigate(`/emails/edit/${notice.emailId}`);
            }
          }}
          onRefresh={refreshMonitor}
        />
      </div>

      <ServiceEmailRequestModal
        isOpen={showForm}
        item={null}
        companies={companies}
        companyFilter={companyFilter}
        senderOptions={senderOptions}
        loadingSenders={Boolean(fetching.senderOptions)}
        loading={Boolean(fetching.store || fetching.update)}
        onClose={closeForm}
        onCompanyFilterChange={handleCompanyFilterChange}
        onSubmit={handleSubmit}
      />

      <ServiceEmailBulkModal
        isOpen={showBulkForm}
        onClose={() => setShowBulkForm(false)}
        onCreated={refreshActiveList}
      />

      {canExportExcel && (
        <EmailsSimpleExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          defaultStartDate={filters.received_date || todayIso()}
          defaultEndDate={filters.received_date || todayIso()}
          extraFilters={{
            search: filters.search || undefined,
            status: filters.status || undefined,
            request_date: filters.request_date || undefined,
            company_id: filters.company_id || undefined,
          }}
        />
      )}

      <ServiceEmailCommentModal
        isOpen={Boolean(commentTarget)}
        onClose={() => setCommentTarget(null)}
        emailId={commentTarget?.id ?? 0}
        subject={commentTarget?.subject}
        onSuccess={refreshActiveList}
      />
    </div>
  );
};

export default EmailsPage;
